/**
 * Dial — a rotatable brass dial that Basil can spin with Turn-Table Touch.
 *
 * Has discrete notch positions. Spinning cycles through notches.
 * When the target notch is reached, emits DIAL_ALIGNED.
 * Can be locked in place with Pneumatic Anchor.
 *
 * Emits DIAL_TURNED events with current notch index.
 */
import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { PBRMaterial } from "@babylonjs/core/Materials/PBR/pbrMaterial";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Animation } from "@babylonjs/core/Animations/animation";
import { InteractiveObject, InteractiveConfig } from "./InteractiveObject";
import type { InstantiatedModel } from "../../systems/AssetManager";
import { gameEvents } from "../../core/EventBus";
import { GameEvents } from "../../config/events";
import { COLORS } from "../../config/GameConfig";

export interface DialConfig extends Omit<InteractiveConfig, "size"> {
  size?: Vector3;
  totalNotches?: number;
  targetNotch?: number;
  /** Optional authored housing placement; logical root and angular rules stay fixed. */
  modelPlacement?: { scale: number; offset: Vector3 };
}

export class Dial extends InteractiveObject {
  private currentNotch = 0;
  private readonly totalNotches: number;
  private readonly targetNotch: number;
  private isLocked = false;

  private dialBody: Mesh;
  private pointerMesh: Mesh;
  private notchMeshes: Mesh[] = [];

  /**
   * Rotation pivot for the procedural pointer. The needle rotates
   * RELATIVE to the static notch ring — the old code yawed rootMesh,
   * which spun pointer and notches together so their relative angle
   * never changed and a spin read as the whole prop twisting (Q-036c).
   */
  private needlePivot: TransformNode;

  /** Needle mesh from the loaded dial.glb (null on procedural fallback). */
  private glbNeedle: AbstractMesh | null = null;
  /** Authored local Z rotation of the GLB needle, captured at load. */
  private glbNeedleBaseZ = 0;
  /** Per-instance clone of the GLB needle material (lock tint target). */
  private glbNeedleMat: PBRMaterial | StandardMaterial | null = null;
  /** The needle material's authored emissive, restored on unlock(). */
  private glbNeedleBaseEmissive: Color3 = Color3.Black();
  private readonly modelPlacement: DialConfig["modelPlacement"];

  constructor(scene: Scene, config: DialConfig) {
    const size = config.size ?? new Vector3(1.0, 0.2, 1.0);
    super(scene, { ...config, size });

    this.totalNotches = config.totalNotches ?? 8;
    this.targetNotch = config.targetNotch ?? 5;
    this.modelPlacement=config.modelPlacement?{scale:config.modelPlacement.scale,offset:config.modelPlacement.offset.clone()}:undefined;

    // Create the dial body (cylinder)
    this.dialBody = MeshBuilder.CreateCylinder(
      `dial_${config.key}_body`,
      { diameter: size.x, height: size.y, tessellation: 32 },
      scene
    );
    this.dialBody.parent = this.rootMesh;
    this.dialBody.position = Vector3.Zero();

    const bodyMat = new StandardMaterial(`dial_${config.key}_bodyMat`, scene);
    bodyMat.diffuseColor = Color3.FromHexString(COLORS.BRASS);
    bodyMat.specularColor = new Color3(0.3, 0.25, 0.1);
    bodyMat.emissiveColor = Color3.Black();
    this.dialBody.material = bodyMat;
    this.material = bodyMat;

    // Create notch marks around the perimeter (small cylinders)
    const radius = size.x * 0.45;
    for (let i = 0; i < this.totalNotches; i++) {
      const angle = (i / this.totalNotches) * Math.PI * 2;
      const notch = MeshBuilder.CreateCylinder(
        `dial_${config.key}_notch_${i}`,
        { diameter: 0.06, height: size.y + 0.02, tessellation: 8 },
        scene
      );
      notch.parent = this.rootMesh;
      notch.position = new Vector3(
        Math.cos(angle) * radius,
        0,
        Math.sin(angle) * radius
      );

      const notchMat = new StandardMaterial(
        `dial_${config.key}_notch_${i}_mat`,
        scene
      );
      notchMat.diffuseColor =
        i === this.targetNotch
          ? Color3.FromHexString(COLORS.EMERALD)
          : Color3.FromHexString(COLORS.COPPER);
      notchMat.emissiveColor =
        i === this.targetNotch
          ? Color3.FromHexString(COLORS.EMERALD).scale(0.3)
          : Color3.Black();
      notchMat.specularColor = Color3.Black();
      notch.material = notchMat;
      this.notchMeshes.push(notch);
    }

    // Pointer needle (a thin elongated box) — parented to the needle
    // pivot, NOT rootMesh, so updatePointerRotation can sweep it past
    // the static notches.
    this.needlePivot = new TransformNode(`dial_${config.key}_needlePivot`, scene);
    this.needlePivot.parent = this.rootMesh;
    this.needlePivot.position = Vector3.Zero();

    this.pointerMesh = MeshBuilder.CreateBox(
      `dial_${config.key}_pointer`,
      { width: 0.05, height: size.y + 0.04, depth: radius * 0.7 },
      scene
    );
    this.pointerMesh.parent = this.needlePivot;
    this.pointerMesh.position = new Vector3(0, 0, radius * 0.35);

    const pointerMat = new StandardMaterial(
      `dial_${config.key}_pointerMat`,
      scene
    );
    pointerMat.diffuseColor = Color3.FromHexString(COLORS.COPPER);
    pointerMat.emissiveColor = Color3.FromHexString(COLORS.COPPER).scale(0.2);
    pointerMat.specularColor = Color3.Black();
    this.pointerMesh.material = pointerMat;

    // Set initial rotation
    this.updatePointerRotation(false);

    // Attempt to load 3D model override (falls back to procedural mesh)
    // Needle/target-notch materials are edited independently. GPU instances
    // ignore per-mesh material assignments; use the cached template's clones.
    this.loadModelOverride("/assets/models/environment/dial.glb");
  }

  /**
   * Spin the dial by one notch. Returns true if now aligned to target.
   */
  spin(): boolean {
    if (this.isLocked || this.state === "solved") return false;

    this.currentNotch = (this.currentNotch + 1) % this.totalNotches;
    this.updatePointerRotation(true);

    const aligned = this.currentNotch === this.targetNotch;

    if (aligned) {
      this.state = "active";
      this.updateMaterialForState();
      gameEvents.emit(GameEvents.DIAL_ALIGNED, this.key);
    } else {
      this.state = "idle";
      this.updateMaterialForState();
    }

    gameEvents.emit(GameEvents.DIAL_TURNED, this.key);
    return aligned;
  }

  /**
   * Lock the dial in place (Pneumatic Anchor effect).
   */
  lock(): void {
    this.isLocked = true;
    // Visual feedback: pointer turns green (procedural + GLB needle)
    const pointerMat = this.pointerMesh.material as StandardMaterial;
    pointerMat.emissiveColor = Color3.FromHexString(COLORS.EMERALD).scale(0.4);
    if (this.glbNeedleMat) {
      this.glbNeedleMat.emissiveColor = Color3.FromHexString(COLORS.EMERALD).scale(0.4);
    }
    gameEvents.emit(GameEvents.DIAL_LOCKED, this.key);
  }

  /**
   * Unlock the dial.
   */
  unlock(): void {
    this.isLocked = false;
    const pointerMat = this.pointerMesh.material as StandardMaterial;
    pointerMat.emissiveColor = Color3.FromHexString(COLORS.COPPER).scale(0.2);
    if (this.glbNeedleMat) {
      this.glbNeedleMat.emissiveColor = this.glbNeedleBaseEmissive.clone();
    }
    gameEvents.emit(GameEvents.DIAL_UNLOCKED, this.key);
  }

  onInteract(abilityId: string): boolean {
    if (abilityId === "turnTableTouch") {
      return this.spin();
    }
    if (abilityId === "pneumaticAnchor") {
      if (this.isLocked) {
        this.unlock();
      } else {
        this.lock();
      }
      return true;
    }
    return false;
  }

  get aligned(): boolean {
    return this.currentNotch === this.targetNotch;
  }

  get locked(): boolean {
    return this.isLocked;
  }

  get notch(): number {
    return this.currentNotch;
  }

  override getHighlightTarget(): AbstractMesh {
    return this.dialBody;
  }

  /**
   * Wire the loaded dial.glb face for runtime feedback:
   *   - Grab the Dial_Needle mesh so updatePointerRotation drives the
   *     authored needle instead of the hidden procedural pointer. The
   *     authored face is vertical/camera-facing (nodes pre-rotated +90°
   *     about X), so the needle rotates about its local Z.
   *   - The GLB ships ONE shared Dial_Notch material — clone it for the
   *     target notch and add the emerald cue at runtime so final art
   *     keeps the "spin to the green notch" readability.
   * Each dial owns editable mesh/material clones from the cached template.
   * The needle and target notch additionally own their local tint materials.
   */
  protected override onModelLoaded(instance: InstantiatedModel): void {
    if(this.modelPlacement){
      const mount=new TransformNode(`dial_${this.key}_modelMount`,this.scene);
      mount.parent=this.rootMesh;mount.scaling.setAll(this.modelPlacement.scale);mount.position.copyFrom(this.modelPlacement.offset);
      for(const root of instance.rootNodes)root.parent=mount;
    }
    const emeraldCue = Color3.FromHexString(COLORS.EMERALD).scale(0.4);
    for (const mesh of instance.meshes) {
      if (mesh.name.includes("Dial_Needle")) {
        // Adopt Euler rotation: glTF nodes import with rotationQuaternion
        // set, which silently wins over `.rotation` writes until nulled.
        if (mesh.rotationQuaternion) {
          mesh.rotation = mesh.rotationQuaternion.toEulerAngles();
          mesh.rotationQuaternion = null;
        }
        this.glbNeedle = mesh;
        this.glbNeedleBaseZ = mesh.rotation.z;
        const needleMat = mesh.material;
        if (needleMat instanceof PBRMaterial || needleMat instanceof StandardMaterial) {
          const clone = needleMat.clone(`${needleMat.name}_${this.key}`);
          if (clone) {
            this.glbNeedleBaseEmissive = clone.emissiveColor.clone();
            mesh.material = clone;
            this.glbNeedleMat = clone;
          }
        }
      } else if (mesh.name.includes(`Dial_Notch_${this.targetNotch}`)) {
        const notchMat = mesh.material;
        if (notchMat instanceof PBRMaterial || notchMat instanceof StandardMaterial) {
          const clone = notchMat.clone(`${notchMat.name}_target_${this.key}`);
          if (clone) {
            clone.emissiveColor = emeraldCue.clone();
            mesh.material = clone;
          }
        }
      }
    }
    // Snap the freshly loaded needle onto the current notch, and re-apply
    // the lock tint if the dial locked before the async load finished.
    this.updatePointerRotation(false);
    if (this.isLocked && this.glbNeedleMat) {
      this.glbNeedleMat.emissiveColor = emeraldCue.clone();
    }
  }

  // ─── Private ─────────────────────────────────────────────────────────

  /**
   * Point the needle at the current notch. NEVER touches rootMesh
   * rotation — the notch ring stays static so a spin reads as the
   * needle sweeping past fixed reference marks (Q-036c).
   *
   * Angle math: notch i sits at θ_i = (i/totalNotches)·2π measured
   * from the face's +X axis. Both the procedural pointer (rest = +Z)
   * and the authored GLB needle (rest = face +Y) rest at θ = π/2, so
   * the pivot yaw is (π/2 − θ_i) and the GLB local-Z spin is
   * (θ_i − π/2) relative to its authored base.
   *
   * GLB CAVEAT: the needle's on-screen sweep direction composes with
   * the glTF root's handedness mirror — the sign of the (θ − π/2) term
   * is UNVERIFIED until the Browser-pane screenshot pass. If the needle
   * sweeps away from the target notch at runtime, negate that term.
   */
  private updatePointerRotation(animate: boolean): void {
    const notchAngle =
      (this.currentNotch / this.totalNotches) * Math.PI * 2;
    const pivotTarget = Math.PI / 2 - notchAngle;
    const glbTarget = this.glbNeedleBaseZ + (notchAngle - Math.PI / 2);

    if (!animate) {
      this.needlePivot.rotation.y = pivotTarget;
      if (this.glbNeedle) {
        this.glbNeedle.rotation.z = glbTarget;
      }
      return;
    }

    const fps = 30;
    const frames = 6; // ~200ms

    // Procedural pivot — one-notch sweep (pivot yaw decreases per notch).
    {
      const current = this.needlePivot.rotation.y;
      let target = pivotTarget;
      if (target >= current) {
        target -= Math.PI * 2;
      }
      const rotAnim = new Animation(
        `dialSpin_${this.key}_${Date.now()}`,
        "rotation.y",
        fps,
        Animation.ANIMATIONTYPE_FLOAT,
        Animation.ANIMATIONLOOPMODE_CONSTANT
      );
      rotAnim.setKeys([
        { frame: 0, value: current },
        { frame: frames, value: target },
      ]);
      this.needlePivot.animations = [rotAnim];
      this.scene.beginAnimation(this.needlePivot, 0, frames, false, 1, () => {
        // Normalize to the canonical angle after the sweep.
        this.needlePivot.rotation.y = pivotTarget;
      });
    }

    // GLB needle — same one-notch sweep on the authored art (local Z
    // increases per notch in this convention).
    if (this.glbNeedle) {
      const needle = this.glbNeedle;
      const current = needle.rotation.z;
      let target = glbTarget;
      if (target <= current) {
        target += Math.PI * 2;
      }
      const glbAnim = new Animation(
        `dialNeedle_${this.key}_${Date.now()}`,
        "rotation.z",
        fps,
        Animation.ANIMATIONTYPE_FLOAT,
        Animation.ANIMATIONLOOPMODE_CONSTANT
      );
      glbAnim.setKeys([
        { frame: 0, value: current },
        { frame: frames, value: target },
      ]);
      needle.animations = [glbAnim];
      this.scene.beginAnimation(needle, 0, frames, false, 1, () => {
        needle.rotation.z = glbTarget;
      });
    }
  }
}
